// frontend/src/components/Edi/EdiMessageLog.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { useTranslation } from '../../context/TranslationContext';
import DataTable from '../Common/DataTable';
import Pagination from '../Common/Pagination';
import SearchableSelect from '../Common/SearchableSelect';
import { ediAPI } from '../../api/client';
import './EdiMessageLog.css';

const MESSAGE_TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'INVRPT', label: 'INVRPT' },
  { value: 'SLSRPT', label: 'SLSRPT' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'SUCCESS', label: 'Sucesso' },
  { value: 'ERROR', label: 'Erro' },
];

const EdiMessageLog = () => {
  const { t } = useTranslation();

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingId, setExportingId] = useState(null);

  const [messageType, setMessageType] = useState('');
  const [status, setStatus] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const loadMessages = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (messageType) params.append('messageType', messageType);
      if (status) params.append('status', status);
      const res = await ediAPI.getMessages(params.toString());
      setMessages(res.data.messages || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || t('edi.load_error', 'Erro ao carregar registo de mensagens EDI.'));
    } finally {
      setLoading(false);
    }
  }, [messageType, status, t]);

  useEffect(() => { setCurrentPage(1); }, [messageType, status]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // A failed blob request still delivers its JSON error body as a Blob, so
  // err.response.data.error is undefined — read it back out as text first.
  const parseBlobError = async (err, fallback) => {
    const data = err?.response?.data;
    try {
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        const json = JSON.parse(await data.text());
        return json?.error || fallback;
      }
    } catch {
      return fallback;
    }
    return err?.response?.data?.error || fallback;
  };

  // Exports ONE log record: the inv_rpt / sls_rpt rows that came from that
  // processed file, joined with the Navision item data on ean_code.
  const handleExport = async (row) => {
    if (exportingId) return;
    try {
      setExportingId(row.id);
      const res = await ediAPI.exportMessage(row.id);

      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const baseName = String(row.filename || `${row.message_type}_${row.id}`)
        .replace(/\.[^.]+$/, '')
        .replace(/[^A-Za-z0-9._-]+/g, '_');
      const a = document.createElement('a');
      a.href = url;
      a.download = `${baseName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      window.alert(await parseBlobError(err, t('edi.export_error', 'Erro ao exportar dados EDI.')));
    } finally {
      setExportingId(null);
    }
  };

  const totalItems = messages.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const pageSlice = messages.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const columns = [
    { header: t('edi.client_name', 'Cliente'), field: 'client_name' },
    { header: t('edi.message_type', 'Tipo'), field: 'message_type', className: 'col-narrow' },
    { header: t('edi.filename', 'Ficheiro'), field: 'filename' },
    {
      header: t('edi.status', 'Estado'),
      field: 'status',
      className: 'col-narrow',
      render: (row) => (
        <span className={`edi-status-badge ${row.status === 'SUCCESS' ? 'active' : 'error'}`}>
          {row.status === 'SUCCESS' ? t('edi.success', 'Sucesso') : t('edi.error_status', 'Erro')}
        </span>
      ),
    },
    { header: t('edi.rows', 'Linhas'), field: 'rows_upserted', className: 'col-narrow' },
    {
      header: t('edi.processed_at', 'Processado em'),
      field: 'processed_at',
      render: (row) => new Date(row.processed_at).toLocaleString('pt-PT'),
    },
    {
      header: t('edi.processed_by', 'Origem'),
      field: 'processed_by',
      className: 'col-narrow',
      render: (row) => (row.processed_by ? t('edi.manual', 'Manual') : t('edi.automatic', 'Automático')),
    },
    {
      header: '',
      field: 'export',
      className: 'col-narrow',
      render: (row) => {
        const canExport = row.status === 'SUCCESS' && (row.message_type === 'INVRPT' || row.message_type === 'SLSRPT');
        return (
          <button
            type="button"
            className="btn btn-secondary btn-sm edi-export-btn"
            disabled={!canExport || exportingId === row.id}
            title={canExport
              ? t('edi.export_hint', 'Exportar esta mensagem com dados do Navision (Excel)')
              : t('edi.export_unavailable', 'Sem dados para exportar')}
            // the row itself is clickable (shows the error text) — don't trigger that
            onClick={(e) => { e.stopPropagation(); handleExport(row); }}
          >
            {exportingId === row.id
              ? <FiRefreshCw size={14} className="edi-spin" />
              : <FiDownload size={14} />}
          </button>
        );
      },
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>{t('edi.messages_title', 'Mensagens EDI')}</h1>
        <p className="page-subtitle">
          {t('edi.messages_subtitle', 'Histórico de ficheiros INVRPT/SLSRPT recebidos e processados.')}
        </p>
      </div>

      <div className="edi-filters">
        <div className="filter-group">
          <label>{t('edi.message_type', 'Tipo')}</label>
          <SearchableSelect
            value={messageType}
            onChange={setMessageType}
            options={MESSAGE_TYPE_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </div>
        <div className="filter-group">
          <label>{t('edi.status', 'Estado')}</label>
          <SearchableSelect
            value={status}
            onChange={setStatus}
            options={STATUS_OPTIONS}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={pageSlice}
        loading={loading}
        error={error}
        onRetry={loadMessages}
        emptyMessage={t('edi.no_messages', 'Nenhuma mensagem EDI encontrada.')}
        onRowClick={(row) => row.error_message && window.alert(row.error_message)}
      />

      {!loading && !error && totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(limit) => { setItemsPerPage(limit); setCurrentPage(1); }}
        />
      )}
    </div>
  );
};

export default EdiMessageLog;